import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Clock, Flag, Lock, X, Trophy, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function RoundStatusBar() {
  const [roundState, setRoundState] = useState(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completedRound, setCompletedRound] = useState(null);
  const [localTimer, setLocalTimer] = useState(null);
  const hasToastedUpcoming = useRef(false);
  const hasToastedInProgress = useRef(false);

  const acknowledgeRound = (roundId) => {
    sessionStorage.setItem('msf_ack_round', roundId);
    setShowCompletionModal(false);
  };

  // Poll round status every 3 seconds
  useEffect(() => {
    const poll = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/round-status`, { withCredentials: true });
        const data = res.data;
        setRoundState(data);

        if (data.status === 'upcoming') {
          if (!hasToastedUpcoming.current) {
            hasToastedUpcoming.current = true;
            toast.info(`Round ${data.round.round_number} starts in ${formatTime(data.countdown_seconds)}!`, { duration: 5000 });
          }
        }

        if (data.status === 'in_progress') {
          setLocalTimer(data.remaining_seconds);
          hasToastedUpcoming.current = false;
          if (!hasToastedInProgress.current) {
            hasToastedInProgress.current = true;
            toast.warning(`Round ${data.round.round_number} is NOW IN PROGRESS! Selections locked.`, { duration: 5000 });
          }
        }

        // Show completion modal for any completed round not yet acknowledged
        if (data.status === 'just_completed' || data.status === 'idle') {
          const completed = data.status === 'just_completed' ? data.round : data.last_completed;
          const acked = sessionStorage.getItem('msf_ack_round');
          if (completed && completed.round_id && acked !== completed.round_id) {
            setCompletedRound(completed);
            setShowCompletionModal(true);
          }
          setLocalTimer(null);
          hasToastedInProgress.current = false;
        }
      } catch {
        // Silent fail
      }
    };

    poll();
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, []);

  // Local countdown timer (decrements every second between polls)
  useEffect(() => {
    if (localTimer === null || localTimer <= 0) return;
    const tick = setInterval(() => {
      setLocalTimer(prev => {
        if (prev <= 1) {
          clearInterval(tick);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [localTimer]);

  // Local countdown for upcoming
  const [upcomingTimer, setUpcomingTimer] = useState(null);
  useEffect(() => {
    if (roundState?.status === 'upcoming') {
      setUpcomingTimer(roundState.countdown_seconds);
    } else {
      setUpcomingTimer(null);
    }
  }, [roundState?.status, roundState?.countdown_seconds]);

  useEffect(() => {
    if (upcomingTimer === null || upcomingTimer <= 0) return;
    const tick = setInterval(() => {
      setUpcomingTimer(prev => {
        if (prev <= 1) { clearInterval(tick); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [upcomingTimer]);

  // Don't render banners when idle, but still render the completion modal if needed
  if (!roundState || (roundState.status === 'idle' && !showCompletionModal)) return null;

  return (
    <>
      {/* Upcoming banner */}
      {roundState.status === 'upcoming' && upcomingTimer > 0 && (
        <div className="bg-yellow-600/90 text-white py-2 px-4 text-center text-sm font-heading font-bold tracking-wide animate-pulse" data-testid="round-upcoming-banner">
          <Clock size={16} className="inline mr-2" />
          ROUND {roundState.round.round_number} STARTS IN {formatTime(upcomingTimer)} &mdash; FINALIZE YOUR TEAM & BOOSTS!
        </div>
      )}

      {/* In-progress bar */}
      {roundState.status === 'in_progress' && (
        <div className="bg-red-700/90 text-white py-2 px-4 text-center text-sm font-heading font-bold tracking-wide" data-testid="round-in-progress-bar">
          <Lock size={16} className="inline mr-2" />
          ROUND {roundState.round.round_number} IN PROGRESS &mdash; {formatTime(localTimer || 0)} REMAINING
          <span className="ml-3 text-xs text-red-200 font-normal">Selections & boosts locked</span>
        </div>
      )}

      {/* Completion modal */}
      <RoundCompleteModal
        open={showCompletionModal}
        round={completedRound}
        onClose={() => acknowledgeRound(completedRound?.round_id)}
      />
    </>
  );
}

function RoundCompleteModal({ open, round, onClose }) {
  const [results, setResults] = useState(null);
  const [showRacing, setShowRacing] = useState(true);

  useEffect(() => {
    if (!open) return;
    const fetchLatestSnapshots = async () => {
      try {
        // Fetch all captured snapshots per league; pick the highest-round OFFICIAL one.
        const [racingRes, motoRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/round-results?league=racing`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/round-results?league=moto`, { withCredentials: true }),
        ]);
        const latest = (snaps) => {
          const official = (snaps || []).filter(s => s.round_type !== 'test');
          if (!official.length) return null;
          return official.sort((a, b) => b.round_number - a.round_number)[0];
        };
        setResults({ racing: latest(racingRes.data), moto: latest(motoRes.data) });
      } catch {
        setResults({ racing: null, moto: null });
      }
    };
    fetchLatestSnapshots();
  }, [open]);

  if (!round) return null;

  const activeSnap = results ? (showRacing ? results.racing : results.moto) : null;
  const sortedScores = activeSnap
    ? [...(activeSnap.player_scores || [])].sort(
        (a, b) => (b.round_points || 0) - (a.round_points || 0)
      )
    : [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-950 border border-zinc-800 max-w-lg p-0 gap-0" onPointerDownOutside={(e) => e.preventDefault()}>
        <div className="h-1 bg-green-500" />

        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white z-10" data-testid="round-complete-close">
          <X size={20} />
        </button>

        <div className="px-8 py-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 flex items-center justify-center bg-green-900/50 border border-green-700">
              <Flag size={24} className="text-green-400" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-xl text-white" data-testid="round-complete-title">
                <DialogTitle asChild>
                  <span>Round {activeSnap?.round_number ?? round.round_number} Results</span>
                </DialogTitle>
              </h2>
              <p className="text-sm text-green-400">
                {activeSnap
                  ? `${activeSnap.round_name || `Round ${activeSnap.round_number}`} — points earned this round`
                  : 'The race is over. Check the standings below.'}
              </p>
            </div>
          </div>

          {/* League toggle */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setShowRacing(true)}
              data-testid="round-complete-racing-tab"
              className={`px-4 py-1.5 text-xs font-heading font-bold uppercase ${showRacing ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`}
            >
              MSF Racing
            </button>
            <button
              onClick={() => setShowRacing(false)}
              data-testid="round-complete-moto-tab"
              className={`px-4 py-1.5 text-xs font-heading font-bold uppercase ${!showRacing ? 'bg-secondary text-black' : 'bg-zinc-800 text-zinc-400'}`}
            >
              MotoMSF
            </button>
          </div>

          {/* Scrollable per-round standings */}
          <div className="max-h-[300px] overflow-y-auto border border-zinc-800" data-testid="round-complete-standings">
            {!results ? (
              <div className="p-4 text-center text-zinc-500">Loading round results...</div>
            ) : !activeSnap ? (
              <div className="p-4 text-center text-zinc-500" data-testid="round-complete-no-snapshot">
                No captured results yet for this league.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-zinc-900 sticky top-0">
                  <tr>
                    <th className="text-left py-2 px-3 text-zinc-400 font-heading">#</th>
                    <th className="text-left py-2 px-3 text-zinc-400 font-heading">Player</th>
                    <th className="text-right py-2 px-3 text-zinc-400 font-heading">Round pts</th>
                    <th className="text-right py-2 px-3 text-zinc-400 font-heading">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedScores.map((p, i) => (
                    <tr
                      key={p.user_id || i}
                      className={`border-t border-zinc-800 ${i < 3 ? 'bg-zinc-900/50' : ''}`}
                      data-testid={`round-complete-row-${i}`}
                    >
                      <td className="py-2 px-3 font-mono font-bold text-zinc-300">
                        {i === 0 ? <Trophy size={14} className="inline text-yellow-400" /> : i + 1}
                      </td>
                      <td className="py-2 px-3 text-white">{p.name || 'Unknown'}</td>
                      <td className={`py-2 px-3 text-right font-mono font-bold ${showRacing ? 'text-primary' : 'text-secondary'}`}>
                        {Math.round(p.round_points || 0)}
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-zinc-400">
                        {Math.round(p.cumulative_points || 0)}
                      </td>
                    </tr>
                  ))}
                  {sortedScores.length === 0 && (
                    <tr><td colSpan={4} className="py-4 text-center text-zinc-500">No player scores in this round</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="px-8 pb-6">
          <button
            onClick={onClose}
            data-testid="round-complete-dismiss"
            className="w-full py-2.5 bg-primary text-white font-heading font-bold uppercase text-sm hover:opacity-80 transition-all"
          >
            Got it!
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
